import Image from "next/image";

import { DemoModeIcon } from "./DemoModeIcon";
import { isFifaSlug } from "@/features/fifa-universe/registry";
import { FifaModeArtLazy } from "@/features/fifa-universe/FifaModeArtLazy";
import { WordGameHeroImage } from "./WordGameHeroImage";

// Every illustrated tile ships in public/. Do not derive artwork locations
// from the authentication server: another environment may not have that bucket.
const GAME_MODE_IMAGE_BASE = "/assets/demos/game-modes";

// Artwork generated 2026-09-05 ships with the app: the dated CDN collection
// does not contain these files. Both FIFA Cards entries share one illustration.
// Prompts + audit: docs/artwork/.
const LOCAL_MODE_IMAGES = new Map([
  ["buscaminas", "buscaminas"],
  ["pistas", "pistas"],
  ["ultimo", "ultimo"],
  ["minuto", "minuto"],
  ["shared-player", "ortak-futbolcu"],
  ["name-chain", "son-harfle"],
  ["tenable", "lab-top-10-knockout"],
  ["football-horserace", "football-horserace"],
  ["football-codenames", "football-codenames"],
  ["dato-falso", "dato-falso"],
  ["dato-falso-futbolero", "dato-falso"],
  ["conexiones-futboleras", "conexiones"],
  ["football-darts-501", "football-501"],
  ["daily-clues", "daily-clues"],
  // Owner 2026-09-15: Card Detective gets the same illustrated treatment as the other dailies (Codex-generated).
  ["daily-cardDetective", "daily-cardDetective"],
  ["daily-putInOrder", "daily-putInOrder"],
  ["daily-fifaCards", "daily-fifaCards"],
  ["mini-guess-fifa-card", "daily-fifaCards"],
  ["lab-own-goal", "lab-own-goal"],
  ["lab-say-it-with-memes", "lab-say-it-with-memes"],
  ["lab-draft-battle", "lab-draft-battle"],
  ["lab-top-10-knockout", "lab-top-10-knockout"],
  ["lab-missing-xi", "lab-missing-xi"],
  ["lab-ball-knowledge", "lab-ball-knowledge"],
  ["lab-bingo-battle", "lab-bingo-battle"],
  ["lab-connections-race", "lab-connections-race"],
  ["lab-stat-501", "lab-stat-501"],
]);

export const ILLUSTRATED_MODE_SLUGS = new Set([
  ...LOCAL_MODE_IMAGES.keys(),
  "weekend-league",
  "auction",
  "mini-final-third",
  "mini-road-to-goal",
  "mini-squad-spin",
  "mini-trivia-spin",
  "mini-penalty-shootout",
  "mini-daily-jackpot",
  "mini-pass-chain",
  "mini-accumulator",
  "mini-squad-collection",
  "mini-bet-slip-booster",
  "mini-half-time-trivia",
  "mini-odds-board",
  "mini-football-grid",
  "mini-survivor",
  "mini-trivia-mines",
  "mini-quiz-board",
  "mini-last-one-standing",
  "mini-golden-goal",
  "mini-career-race",
  "mini-stat-sniper",
  "mini-guess-the-goal",
  "daily-moneyDrop",
  "daily-cardDetective",
  "daily-trueFalse",
  "daily-countdown",
  "daily-imposter",
  "daily-careerPath",
  "daily-highLow",
  "daily-footballLogic",
]);

// Vibrant, dark-friendly gradient pairs. Each game maps to one stably (by slug
// hash) so its tile colour never changes, while the set gives the grid variety.
const GRADIENTS: ReadonlyArray<readonly [string, string]> = [
  ["#3B4CCA", "#7C3AED"], // indigo → violet
  ["#0EA5E9", "#2563EB"], // sky → blue
  ["#0F9D8C", "#0D7D6B"], // teal
  ["#F59E0B", "#EA580C"], // amber → orange
  ["#EC4899", "#BE185D"], // pink
  ["#65A30D", "#15803D"], // lime → green
  ["#8B5CF6", "#6D28D9"], // purple
  ["#06B6D4", "#3B82F6"], // cyan → blue
  ["#F97316", "#DC2626"], // orange → red
  ["#14B8A6", "#0891B2"], // teal → cyan
];

function hash(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) h = (h * 31 + input.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * A designed "hero" tile for a demo card — the game's core-element glyph
 * (DemoModeIcon) on a vibrant gradient with a glow, dot pattern and an
 * oversized watermark of the same glyph. Conveys the game at a glance without
 * a screenshot. `className` controls the aspect ratio.
 */
// The daily reuses the prototype card's artwork.
const ART_ALIAS: Record<string, string> = { "daily-missingXi": "lab-missing-xi", "daily-passChain": "mini-pass-chain", "daily-statSniper": "mini-stat-sniper" };

export function DemoModeArt({ slug: rawSlug, className = "", priority = false, sizes = "(min-width: 1024px) 50vw, 100vw" }: { slug: string; className?: string; priority?: boolean; sizes?: string }) {
  const slug = ART_ALIAS[rawSlug] ?? rawSlug;
  // Owner decision 2026-09-05: the Guess the Card daily uses the FIFA
  // collection's tile art (a real masked gold card on the pitch gradient).
  if (isFifaSlug(slug) || slug === "mini-guess-fifa-card" || slug === "daily-fifaCards") {
    return <FifaModeArtLazy slug={slug === "daily-fifaCards" ? "mini-guess-fifa-card" : slug} className={className} glyph={slug !== "daily-fifaCards"} />;
  }
  // Ranked (the "match" engine) shows the hub hero's trophy on brand green.
  if (slug === "match") {
    return (
      <div className={`relative flex items-center justify-center overflow-hidden bg-brand-green ${className}`} aria-hidden>
        <Image src="/assets/brand/ranked-hands-trophy.svg" alt="" width={257} height={294} className="h-[88%] w-auto object-contain object-bottom translate-y-[8%]" />
      </div>
    );
  }
  if (ILLUSTRATED_MODE_SLUGS.has(slug)) {
    return (
      <div className={`relative overflow-hidden bg-game-art-night ${className}`} aria-hidden>
        {priority && (slug === "name-chain" || slug === "shared-player") ? (
          <WordGameHeroImage slug={slug} sizes={sizes} />
        ) : <Image
          src={LOCAL_MODE_IMAGES.has(slug)
            ? `/assets/demos/game-modes/${LOCAL_MODE_IMAGES.get(slug)}.webp`
            : `${GAME_MODE_IMAGE_BASE}/${slug}.webp`}
          alt=""
          fill
          sizes={sizes}
          quality={priority ? 60 : 75}
          preload={priority}
          fetchPriority={priority ? "high" : undefined}
          loading={priority ? undefined : "lazy"}
          className="object-contain object-center"
        />}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/30 to-transparent" />
      </div>
    );
  }

  const [from, to] = GRADIENTS[hash(slug) % GRADIENTS.length];

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }}
      aria-hidden
    >
      {/* soft top-left highlight */}
      <div
        className="pointer-events-none absolute -left-8 -top-10 size-40 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(255,255,255,0.38), transparent 68%)" }}
      />
      {/* dotted texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.85) 1px, transparent 1px)",
          backgroundSize: "13px 13px",
        }}
      />
      {/* oversized watermark of the same glyph, bottom-right */}
      <DemoModeIcon
        slug={slug}
        className="pointer-events-none absolute -bottom-7 -right-6 size-40 text-white/12"
      />
      {/* main glyph */}
      <div className="absolute inset-0 flex items-center justify-center">
        <DemoModeIcon
          slug={slug}
          className="size-14 text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)] sm:size-16"
        />
      </div>
      {/* bottom fade so overlaid text/badges keep contrast */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/25 to-transparent" />
    </div>
  );
}
